import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  Branch,
  BranchRole,
  HasItems,
  UserLocationResponse,
  LocationRole,
  Pagination,
  PaginationRequest,
  SystemRole,
  UserProfile,
  UserRoles,
} from '../types';

@Injectable({
  providedIn: 'root',
})
export class UserAdminService {
  private http = inject(HttpClient);
  private apiHost = `${environment.apiBaseUrl}`;

  private _cachedBranches = signal<Branch[] | undefined>(undefined);
  private _cachedLocations = signal<UserLocationResponse[] | undefined>(undefined);

  getUserById(userId: string): Observable<UserProfile> {
    return this.http.get<UserProfile>(`${this.apiHost}/UserManagement/Users/${userId}`);
  }

  getUsersForLocation(locationId: number): Observable<HasItems<UserProfile>> {
    return this.http.get<HasItems<UserProfile>>(`${this.apiHost}/UserManagement/Locations/${locationId}/Users`);
  }

  getUsersForBranch(branchId?: number): Observable<HasItems<UserProfile>> {
    const requestUrl = `${this.apiHost}/UserManagement/Branches/${branchId}/Users`;
    return this.http.get<HasItems<UserProfile>>(requestUrl);
  }

  getMyUsersForBranches(paginationRequest: PaginationRequest, includeInternal = false): Observable<Pagination<UserProfile>> {
    const params = {
      ...paginationRequest,
      include_internal: includeInternal,
    };

    return this.http.get<Pagination<UserProfile>>(`${this.apiHost}/UserManagement/Me/Users`, {
      params: new HttpParams({ fromObject: params as unknown as Record<string, string | number> }),
    });
  }

  getMyBranches(allowCached = false): Observable<Branch[]> {
    if (allowCached && this._cachedBranches()) {
      return new Observable<Branch[]>((observer) => {
        observer.next(this._cachedBranches()!);
        observer.complete();
      });
    }

    return this.http.get<Branch[]>(`${this.apiHost}/UserManagement/Me/Branches`).pipe(
      tap((branches) => {
        this._cachedBranches.set(branches);
      }),
    );
  }

  getMyLocations(allowCached = false): Observable<UserLocationResponse[]> {
    if (allowCached && this._cachedLocations()) {
      return new Observable<UserLocationResponse[]>((observer) => {
        observer.next(this._cachedLocations()!);
        observer.complete();
      });
    }

    return this.http.get<UserLocationResponse[]>(`${this.apiHost}/UserManagement/Me/Locations`).pipe(
      tap((locations) => {
        this._cachedLocations.set(locations);
      }),
    );
  }

  createUser(name: string, email: string): Observable<UserProfile> {
    return this.http.post<UserProfile>(`${this.apiHost}/UserManagement/Users`, { name, email });
  }

  grantSystemRole(userId: string, role: SystemRole): Observable<UserProfile> {
    return this.http.put<UserProfile>(`${this.apiHost}/UserManagement/Users/${userId}/Roles/${role}`, {});
  }

  revokeSystemRole(userId: string, role: SystemRole): Observable<UserProfile> {
    return this.http.delete<UserProfile>(`${this.apiHost}/UserManagement/Users/${userId}/Roles/${role}`);
  }

  grantBranchRole(userId: string, branchId: string, role: BranchRole): Observable<UserProfile> {
    return this.http.put<UserProfile>(`${this.apiHost}/UserManagement/Branches/${branchId}/Users/${userId}/Roles/${role}`, {});
  }

  revokeBranchRole(userId: string, branchId: string, role: BranchRole): Observable<UserProfile> {
    return this.http.delete<UserProfile>(`${this.apiHost}/UserManagement/Branches/${branchId}/Users/${userId}/Roles/${role}`);
  }

  grantLocationRole(userId: string, locationId: string, role: LocationRole): Observable<UserProfile> {
    return this.http.put<UserProfile>(`${this.apiHost}/UserManagement/Locations/${locationId}/Users/${userId}/Roles/${role}`, {});
  }

  revokeLocationRole(userId: string, locationId: string, role: LocationRole): Observable<UserProfile> {
    return this.http.delete<UserProfile>(`${this.apiHost}/UserManagement/Locations/${locationId}/Users/${userId}/Roles/${role}`);
  }

  updatePermissions(userId: string, permissions: UserRoles): Observable<UserProfile> {
    return this.http.put<UserProfile>(`${this.apiHost}/UserManagement/Users/${userId}/Roles`, permissions);
  }
}
